import React from 'react';
import ReactDOM from 'react-dom/client';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import App from './App';
import { ExternalIdConfigurationError, loadExternalIdConfiguration } from './authConfig';

import './main.css';

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Unable to find the #root element to mount the React application.');
}

const root = ReactDOM.createRoot(rootElement as HTMLElement);

interface ConfigErrorScreenProps {
  message: string;
  missing?: string[];
}

function ConfigErrorScreen({ message, missing }: ConfigErrorScreenProps) {
  return (
    <div className="auth-screen">
      <div className="auth-card">
        <h1>Configuration required</h1>
        <p>
          The frontend cannot start because the Microsoft Entra External ID environment variables are missing or incomplete.
          Update the <code>.env</code> file locally or the Static Web Apps configuration before reloading the page.
        </p>
        <pre className="auth-error-details">{message}</pre>
        <div className="auth-required-keys">
          <span>Required keys:</span>
          <ul>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_TENANT_NAME</code>
            </li>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_TENANT_ID</code>
            </li>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_SIGNIN_USER_FLOW</code>{' '}
              <span className="auth-required-alt">(or <code>VITE_ENTRA_EXTERNAL_ID_AUTHORITY_PATH</code>)</span>
            </li>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_CLIENT_ID</code>
            </li>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_API_SCOPE</code>
            </li>
            <li>
              <code>VITE_ENTRA_EXTERNAL_ID_AUTHORITY_URL</code> or{' '}
              <code>VITE_ENTRA_EXTERNAL_ID_AUTHORITY_DOMAIN</code>
            </li>
          </ul>
        </div>
        {missing && missing.length > 0 ? (
          <p className="auth-error-note">
            Double-check the Static Web Apps application settings or workflow <code>env</code> values for the missing key
            {missing.length > 1 ? 's' : ''} listed above.
          </p>
        ) : null}
      </div>
    </div>
  );
}

async function bootstrap() {
  try {
    const authConfig = loadExternalIdConfiguration();
    const msalInstance = new PublicClientApplication(authConfig.msalConfig);

    try {
      await msalInstance.initialize();
      const redirectResult = await msalInstance.handleRedirectPromise();

      const account =
        redirectResult?.account ??
        msalInstance.getActiveAccount() ??
        msalInstance.getAllAccounts()[0] ??
        null;

      if (account) {
        msalInstance.setActiveAccount(account);
      }
    } catch (initError) {
      console.error('Failed to establish Microsoft Entra External ID session', initError);
    }

    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <App authConfig={authConfig} />
        </MsalProvider>
      </React.StrictMode>
    );
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const missing = error instanceof ExternalIdConfigurationError ? error.missing : undefined;
    console.error('Failed to initialize Microsoft Entra External ID configuration', error);

    root.render(
      <React.StrictMode>
        <ConfigErrorScreen message={message} missing={missing} />
      </React.StrictMode>
    );
  }
}

void bootstrap();
